import React from "react";
import { useNavigate } from "react-router-dom";
import { thdfApi } from "../lib/api/handlers/thdf";
import type { ThdfResponse } from "../lib/api/types";
import { PATHS, buildViewPath } from "../routes/paths";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** QR contents identify a record; all displayed details come from the staff API. */
export default function ThdfQrLookup({ id, onOpen }: { id: string; onOpen?: () => void }) {
  const navigate = useNavigate();
  const [attempt, setAttempt] = React.useState(0);
  const [state, setState] = React.useState<{ record?: ThdfResponse; error?: string }>({});

  React.useEffect(() => {
    let active = true;
    setState({});
    if (!UUID.test(id)) {
      setState({ error: "This QR does not contain a valid declaration reference." });
      return;
    }
    thdfApi.getForQr(id).then((record) => {
      if (active) setState(record?.id === id
        ? { record }
        : { error: "The declaration response did not match this QR. Please try again." });
    }).catch((error) => {
      if (!active) return;
      const status = error?.status;
      const message = status === 401 ? "Your session has expired. Sign in again to retrieve the declaration."
        : status === 403 ? "You do not have permission to access this declaration."
        : status === 404 ? "No declaration was found for this reference."
        : "Unable to retrieve the declaration. Check your connection and try again.";
      setState({ error: message });
    });
    return () => { active = false; };
  }, [id, attempt]);

  if (state.error) return <div role="alert" className="mt-4 rounded-xl bg-rose-50 p-4 text-rose-700">
    <p>{state.error}</p>
    <button type="button" className="mt-2 underline" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
  </div>;
  const record = state.record;
  if (!record) return <p role="status" className="mt-4">Retrieving declaration…</p>;

  return <div className="mt-4 space-y-4 rounded-xl border p-4">
    <h3 className="font-semibold">Traveller declaration</h3>
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      {[
        ["Reference", record.id], ["Traveller", record.name],
        ["Passport / ID", record.passportNo], ["Nationality", record.nationality],
        ["Port of entry", record.borderPost], ["Arrival date", record.arrivalDate],
        ["Status", record.actionTaken || "Pending review"],
      ].map(([label, value]) => <div key={label}><dt className="text-slate-500">{label}</dt><dd className="break-words font-medium">{value || "—"}</dd></div>)}
    </dl>
    <p className="text-sm text-slate-600">A declaration reference is not health clearance. Review the saved form and complete the required processes.</p>
    <button type="button" className="rounded-lg bg-emerald-700 px-4 py-2 font-semibold text-white" onClick={() => {
      onOpen?.();
      navigate(buildViewPath(PATHS.THDF_CASES_CREATE, record.id));
    }}>Open declaration</button>
  </div>;
}
